import { useSideEffectOnMount } from "@web-monorepo/hooks";
import { Trans, t } from "@web-monorepo/i18n/macro";
import * as logClient from "@classdojo/log-client";
import { DDSButton, DDSDisplayText, DDSHeadlineText } from "@web-monorepo/dds";
import ToolkitAppScroller from "components/BeyondToolkit/ToolkitAppScroller";
import { IconShuffle } from "@web-monorepo/dds-icons";
import ToolkitCard from "components/ToolkitCard";
import { useMemo, useRef, useState } from "react";
import { logParentToolkitEvent } from "src/utils/logEvent";
import { generateLoadingAnimation, randomIndex } from "utils";
import { TABLE_TOPICS } from "utils/beyondToolkitTools";
import { sendUsage } from "utils/freemium";
import styles from "./index.module.css";

const rolling = "/static/media/rolling-loop.mp3";
const finish = "/static/media/finish.mp3";

// eslint-disable-next-line @web-monorepo/limit-use-state-calls -- initial burndown
function TableTopics() {
  const questions = useMemo(() => {
    return generateQuestions();
  }, []);

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(() => randomIndex(questions));
  const [loading, setLoading] = useState(true);
  const [emojiIndex, setEmojiIndex] = useState(() => randomIndex(questions));
  const [showQuestion, setShowQuestion] = useState(true);

  const blinkQuestion = useRef<number | undefined>();
  const rollingAudioNode = useRef<HTMLAudioElement | null>(null);
  const finishAudioNode = useRef<HTMLAudioElement | null>(null);

  const newQuestion = () => {
    if (rollingAudioNode.current) {
      rollingAudioNode.current.play().catch(() => {
        logClient.sendMetrics([
          {
            type: "increment",
            value: 1,
            metricName: "toolkit.media.audio.play.error",
          },
        ]);
      });
    }
    setRandomQuestion();
    rerunAnimation();
    displayQuestion();
  };

  const updateEmoji = () => {
    setEmojiIndex(randomIndex(emojis));
  };

  const rerunAnimation = generateLoadingAnimation({
    numSegments: 6,
    endIntervalDuration: 100,
    startIntervalDuration: 300,
    onAnimationStateChange: setLoading,
    onAnimationTick: updateEmoji,
  });

  const setRandomQuestion = () => {
    setCurrentQuestionIndex(randomIndex(questions));
  };

  const displayQuestion = () => {
    setTimeout(() => {
      if (finishAudioNode.current) finishAudioNode.current.play();
      blinkQuestion.current = window.setInterval(() => {
        setShowQuestion((showQuestion) => !showQuestion);
      }, 200);
      window.setTimeout(() => {
        setShowQuestion(true);
        window.clearInterval(blinkQuestion.current);
        blinkQuestion.current = undefined;
      }, 800);
    }, 3000);
    // animation lasts for 3 seconds, so want to wait for that before displaying blinking text
  };

  useSideEffectOnMount(() => {
    newQuestion();
    sendUsage("dinnerTopics");
  });

  const { title, imgSrc } = TABLE_TOPICS();

  return (
    <ToolkitAppScroller title={title} variant="dinnerTopics" imgSrc={imgSrc}>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption -- decorative audio for classroom activity */}
      <audio src={rolling} ref={rollingAudioNode} />
      {/* eslint-disable-next-line jsx-a11y/media-has-caption -- decorative audio for classroom activity */}
      <audio src={finish} ref={finishAudioNode} />
      <ToolkitCard>
        <div className={styles.questionWrapper}>
          {loading ? (
            <DDSDisplayText>{emojis[emojiIndex]}</DDSDisplayText>
          ) : (
            showQuestion && (
              <DDSHeadlineText as="h2" level="md">
                {questions[currentQuestionIndex]}
              </DDSHeadlineText>
            )
          )}
        </div>
        {loading ? (
          <DDSButton data-name="index:table_topics:toolkit.table_topics.shuffling" variant="secondary" disabled>
            <Trans>Shuffling...</Trans>
          </DDSButton>
        ) : (
          <DDSButton
            variant="unstyled"
            data-name="index:table_topics:ideas_shuffle_icon"
            className={styles.shuffleButton}
            onClick={() => {
              logParentToolkitEvent({
                eventName: "table_topics.new_question.tap",
              });
              newQuestion();
            }}
          >
            <IconShuffle className={styles.shuffleIcon} />
            <Trans>New question</Trans>
          </DDSButton>
        )}
      </ToolkitCard>
    </ToolkitAppScroller>
  );
}

export default TableTopics;

const generateQuestions = () => [
  t`What are 3 words you would use to describe yourself?`,
  t`What do you think you are good at?`,
  t`Describe the best thing that happened to you in the past week.`,
  t`If you could make up a new subject at school, what would it be?`,
  t`Share something that made you laugh this week.`,
  t`If you had one superpower what would it be? Why?`,
  t`If someone gave you $50 what would you do with it?`,
  t`If you could be any animal what would you be? Why?`,
  t`What is something you would like to learn?`,
  t`Share something you learned today.`,
  t`If you could design your own playground, what would it look like?`,
  t`Describe your favorite game.`,
  t`Who is the nicest person you know?`,
  t`If you could change one thing about your bedroom what would it be?`,
  t`If you were stranded on a desert island and you could only have 1 item, what would it be? Why?`,
  t`Would you rather eat your least favorite vegetable or have extra homework?`,
  t`Share something you did this week that you want to do again.`,
  t`Which is better, being invisible or being able to fly? Why?`,
  t`If we could only have one holiday, which one would you pick? Why?`,
  t`When you are sad how do you cheer yourself up?`,
  t`What is one thing you couldn’t live without. Why?`,
  t`If you could go anywhere in the world, where would you go? Why?`,
  t`What is the longest word you know how to spell?`,
  t`When was the last time you told someone “thank you”? What was it for?`,
  t`Share what you like most about one family member.`,
  t`What is your favorite day of the week? Why?`,
  t`Would you rather sneeze all day or have hiccups all day?`,
  t`What is something you wish you could do right now?`,
  t`If you could pick your own name, what would it be? Why?`,
  t`When was the last time you gave someone a high-five? Who was it? What was it for?`,
  t`Would you ever wear your clothes inside-out at school?`,
  t`What is your biggest fear?`,
  t`If you could only have one wish, what would it be? Why?`,
  t`If you could play with only one thing your whole life, what would it be? Why?`,
  t`What scares you the most? Why?`,
  t`Can you remember a time when you had a really good day? What made it so good?`,
  t`What do you usually do during recess?`,
  t`If you could be great at something, what would you want to be great at? Why?`,
  t`If you could be one age for the rest of your life, what age would you be? Why?`,
  t`Would you rather take a picture or be in the picture?`,
  t`What is your favorite joke?`,
  t`If you could make up a holiday, what would it be called? What would you celebrate?`,
  t`What is your favorite memory? Why?`,
  t`What is your favorite book? What do you like about it?`,
  t`If you could make one improvement to the world, what would it be? Why?`,
  t`What is your funniest face?`,
  t`What is your favorite thing about yourself?`,
  t`Describe your perfect dessert.`,
  t`Who is the funniest person at the table?`,
  t`What relative do you love seeing the most? Why?`,
  t`If you could make a zoo, which animal would you have the most of?`,
  t`When was the last time you helped your parents without being asked?`,
  t`What makes someone a good friend?`,
  t`What’s your favorite silly sound to make?`,
  t`What has been the happiest day of your life?`,
  t`If you could change one thing in the world, what would you change?`,
  t`What is your favorite season? Why?`,
  t`What cartoon character would you most like to be and why?`,
  t`What is the best thing about being your age?`,
  t`Describe your perfect day.`,
  t`Who is your best friend? Why are they your best friend?`,
  t`What do you want to be when you grow up?`,
  t`Is there anything you wish you had done differently today?`,
  t`What is the best thing about being a part of this family?`,
  t`Tell us something that made you happy today.`,
  t`Tell us something nice that you did for someone else today.`,
  t`If you could have any pet, what would you choose and why?`,
  t`If you could be famous for a week, what would you be famous for and why?`,
  t`If our family had a motto or slogan, what would it be?`,
  t`If you could make a movie, what would it be about?`,
  t`What are 3 things you would take if you had to leave your home in an emergency?`,
  t`Share your favorite quality about each person at the table.`,
  t`What are two things you like about yourself and one thing you’d like to work on?`,
  t`If you could outlaw one vegetable, what would it be?`,
  t`Would you rather be the funniest person or the smartest person alive?`,
  t`What is your favorite month? What do you love most about it?`,
  t`If you had to eat the same dinner for the rest of your life, what would you eat?`,
  t`If you could have lunch with anyone in the world, who would it be? What would you ask them?`,
  t`What is your favorite song right now? What do you like most about it?`,
  t`Would you rather be older or younger? Why?`,
  t`Share something you admire about someone sitting at the table.`,
  t`What makes you feel loved?`,
  t`What was your favorite part of today? Why?`,
  t`If you could speak any language what would it be? Why?`,
  t`What is your favorite part about school? What is your least favorite part?`,
  t`What do you wish you did today that you didn’t do?`,
  t`Describe your life 10 years from now.`,
  t`What is your favorite thing to do as a family?`,
  t`What is one activity you wish our family could do this month?`,
  t`Who is one of your heroes and why do they inspire you?`,
  t`What is the scariest thing that ever happened to you?`,
  t`Have you ever stood up for something that you thought was right?`,
  t`What do children understand better than adults?`,
  t`What makes someone brave?`,
  t`If you could build your own house, what would it look like?`,
  t`What’s something you are looking forward to in the next few weeks?`,
  t`What makes a good morning for you?`,
  t`What are you most proud of and why?`,
  t`If you could go back in time, what would you do differently?`,
  t`If you got a puppy today what would you name it?`,
];

const emojis = [
  "🍓",
  "🎉",
  "👻",
  "✌️",
  "🐶",
  "🐱",
  "🐝",
  "🐳",
  "🌵",
  "🍄",
  "🍁",
  "🌼",
  "⭐️",
  "🔥",
  "🌎",
  "⛄️",
  "❄️",
  "🍎",
  "🍉",
  "🐙",
  "🐼",
  "🐯",
  "🍿",
  "⚽️",
  "🏀",
  "🎨",
  "🎹",
  "🥁",
  "🚀",
  "🏝",
  "💎",
  "🎁",
  "📓",
  "✏️",
];
